import { useState } from "react";
import { FormattedMessage } from "react-intl";
import Alert from "reactstrap/lib/Alert";
import Button from "reactstrap/lib/Button";
import Col from "reactstrap/lib/Col";
import Row from "reactstrap/lib/Row";
import Table from "reactstrap/lib/Table";
import {
  AnalysisTabs,
  BarRow,
  FindingList,
  PanelCard,
  findingsForTab,
  formatNumber,
  formatSeconds,
} from "./DailyAnalysisParts";

// Mirrors LogSources in tools/analytics/config.json. Listed here rather than derived from
// data.sources so the chips keep the same order on a day where a source went silent.
const SOURCES = ["iis", "lwcapi", "auth", "lwcscorm"];

function DailyAnalysisBackend({ data }) {
  const [source, setSource] = useState("all");

  const backendFindings = findingsForTab(data, AnalysisTabs.BACKEND);
  const shown =
    source === "all"
      ? backendFindings
      : backendFindings.filter((f) => f.source === source);

  const usage = data.usage || {};
  const statusCodes = usage.statusCodes || [];
  const unauthorized = usage.unauthorizedByEndpoint || [];
  const slowest = usage.slowestApi || [];
  const methods = usage.requestsByMethod || [];
  const appLogs = data.appLogPatterns || [];

  const maxStatus = Math.max(...statusCodes.map((s) => s.count), 0);
  const maxUnauthorized = Math.max(...unauthorized.map((u) => u.count), 0);
  const maxMethod = Math.max(...methods.map((m) => m.count), 0);

  // Three panels show IIS data and hide together when an app source is selected.
  const showIis = source === "all" || source === "iis";

  const missing =
    data.sources && data.sources.missing ? data.sources.missing : [];
  const silent =
    source === "all" ? missing : missing.filter((name) => name === source);

  return (
    <div>
      <div className="da-chips">
        <Button
          outline={source !== "all"}
          color="primary"
          onClick={() => setSource("all")}
        >
          <FormattedMessage id="All" />
        </Button>
        {SOURCES.map((name) => (
          <Button
            key={name}
            outline={source !== name}
            color="primary"
            onClick={() => setSource(name)}
          >
            {name}
            <span className="da-chips__count">
              {backendFindings.filter((f) => f.source === name).length}
            </span>
          </Button>
        ))}
      </div>

      {/* A silent application log is not a healthy service - it is an unmeasured day,
          and the page must say so instead of showing a green tick. */}
      {silent.length > 0 ? (
        <Alert color="warning" className="da-banner">
          <FormattedMessage
            id="DailyAnalysisSourceSilent"
            values={{ source: silent.join(", ") }}
          />
        </Alert>
      ) : (
        ""
      )}

      {showIis ? (
        <Row className="da-equal">
          <Col lg="6">
            <PanelCard
              titleId="DailyAnalysisStatusCodes"
              subtitle={
                <FormattedMessage
                  id="DailyAnalysisCallsCount"
                  values={{
                    count: formatNumber(data.summary && data.summary.requests),
                  }}
                />
              }
            >
              {statusCodes.map((row) => (
                <BarRow
                  key={row.status}
                  label={row.status}
                  value={row.count}
                  max={maxStatus}
                  tone={
                    row.status.charAt(0) === "5"
                      ? "crit"
                      : row.status.charAt(0) === "4"
                        ? "warn"
                        : null
                  }
                />
              ))}
            </PanelCard>
          </Col>
          <Col lg="6">
            <PanelCard titleId="DailyAnalysisUnauthorizedByEndpoint">
              {unauthorized.map((row) => (
                <BarRow
                  key={row.endpoint}
                  label={row.endpoint}
                  value={row.count}
                  max={maxUnauthorized}
                  tone="crit"
                />
              ))}
            </PanelCard>
          </Col>
        </Row>
      ) : (
        ""
      )}

      {showIis ? (
        <PanelCard
          titleId="DailyAnalysisSlowestApi"
          subtitle={<FormattedMessage id="DailyAnalysisSlowestApiSub" />}
        >
          <Table responsive size="sm" className="da-table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="DailyAnalysisEndpoint" />
                </th>
                <th className="da-num">
                  <FormattedMessage id="DailyAnalysisP95" />
                </th>
                <th className="da-num">
                  <FormattedMessage id="DailyAnalysisMax" />
                </th>
                <th className="da-num">
                  <FormattedMessage id="DailyAnalysisCalls" />
                </th>
              </tr>
            </thead>
            <tbody>
              {slowest.map((row) => (
                <tr key={row.endpoint}>
                  <td className="da-mono">{row.endpoint}</td>
                  <td className="da-num da-num--strong">
                    {formatSeconds(row.p95Ms)}
                  </td>
                  <td className="da-num">{formatSeconds(row.maxMs)}</td>
                  <td className="da-num">{formatNumber(row.count)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </PanelCard>
      ) : (
        ""
      )}

      {showIis && methods.length > 0 ? (
        <PanelCard
          titleId="DailyAnalysisByMethod"
          subtitle={<FormattedMessage id="DailyAnalysisByMethodSub" />}
        >
          {methods.map((row) => (
            <BarRow
              key={row.method}
              label={row.method}
              value={row.count}
              max={maxMethod}
            />
          ))}
        </PanelCard>
      ) : (
        ""
      )}

      <h6 className="da-section-title">
        <FormattedMessage id="DailyAnalysisFindings" />
      </h6>
      <FindingList findings={shown} />

      {appLogs.length > 0 ? (
        <PanelCard titleId="DailyAnalysisAppLogs">
          <Table responsive size="sm" className="da-table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="DailyAnalysisPattern" />
                </th>
                <th className="da-num">
                  <FormattedMessage id="DailyAnalysisCount" />
                </th>
                <th>
                  <FormattedMessage id="DailyAnalysisSource" />
                </th>
              </tr>
            </thead>
            <tbody>
              {appLogs
                .filter((row) => source === "all" || row.source === source)
                .map((row) => (
                  <tr key={`${row.source}|${row.level}|${row.pattern}`}>
                    <td className="da-mono">
                      <div
                        className="da-clamp"
                        title={row.patternFull || row.pattern}
                      >
                        {row.patternFull || row.pattern}
                      </div>
                    </td>
                    <td className="da-num da-num--strong">
                      {formatNumber(row.count)}
                    </td>
                    <td className="da-dim">
                      {row.source} ·{" "}
                      <span
                        className={
                          row.level === "ERROR" ? "da-level--error" : ""
                        }
                      >
                        {row.level}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </PanelCard>
      ) : (
        ""
      )}
    </div>
  );
}

export default DailyAnalysisBackend;
